import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Inbox,
  Loader,
  ShieldAlert,
  WifiOff,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ElementType, ReactNode } from 'react';
import { cx } from '../cx.ts';

/* ───────── Surfaces ───────── */

export function Card({
  children,
  className,
  as: Tag = 'section',
  glow,
  ...rest
}: { children?: ReactNode; className?: string; as?: ElementType; glow?: boolean } & Record<
  string,
  unknown
>) {
  return (
    <Tag
      {...rest}
      className={cx(
        'hx-card relative overflow-hidden rounded-lg border border-line bg-surface-1',
        glow && 'hx-glow',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Surface({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('rounded-md bg-surface-2 border border-line', className)}>{children}</div>
  );
}

/* ───────── Status (icon + text, never colour alone) ───────── */

export type Status =
  'pending' | 'processing' | 'completed' | 'rejected' | 'refunded' | 'hold' | 'correction' | 'info';

const STATUS: Record<Status, { label: string; icon: LucideIcon; tone: string }> = {
  pending: { label: 'در انتظار بررسی', icon: Clock3, tone: 'text-pending bg-accent-tint' },
  processing: { label: 'در حال انجام', icon: Loader, tone: 'text-pending bg-accent-tint' },
  completed: { label: 'انجام شد', icon: CheckCircle2, tone: 'text-positive bg-positive-tint' },
  rejected: { label: 'رد شد', icon: XCircle, tone: 'text-negative bg-negative-tint' },
  refunded: { label: 'بازگشت داده شد', icon: CircleDashed, tone: 'text-fg-muted bg-surface-2' },
  hold: { label: 'بررسی امنیتی', icon: ShieldAlert, tone: 'text-pending bg-accent-tint' },
  correction: {
    label: 'نیاز به اصلاح',
    icon: AlertTriangle,
    tone: 'text-negative bg-negative-tint',
  },
  info: { label: 'اطلاع', icon: CircleDashed, tone: 'text-info bg-surface-2' },
};

export function StatusBadge({ status, label }: { status: Status; label?: string }) {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap',
        s.tone,
      )}
    >
      <Icon
        aria-hidden
        className={cx(
          'size-3.5',
          status === 'processing' && 'hx-pulse animate-[hx-pulse_1.6s_ease-in-out_infinite]',
        )}
      />
      {label ?? s.label}
    </span>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'positive' | 'negative';
}) {
  const tones = {
    neutral: 'bg-surface-2 text-fg-muted',
    accent: 'bg-accent-tint text-accent-text',
    positive: 'bg-positive-tint text-positive',
    negative: 'bg-negative-tint text-negative',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/* ───────── Skeleton ───────── */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx(
        'hx-skeleton block rounded-sm bg-[linear-gradient(90deg,var(--hx-surface2)_25%,var(--hx-surface3)_50%,var(--hx-surface2)_75%)] bg-[length:200%_100%] animate-[hx-shimmer_1.4s_linear_infinite]',
        className,
      )}
    />
  );
}

/* ───────── State views (empty / error / offline / unavailable) ───────── */

export type StateKind = 'empty' | 'error' | 'offline' | 'unavailable';
const STATE_ICON: Record<StateKind, LucideIcon> = {
  empty: Inbox,
  error: AlertTriangle,
  offline: WifiOff,
  unavailable: CircleDashed,
};

export function StateView({
  kind,
  title,
  description,
  action,
  correlationId,
  compact,
}: {
  kind: StateKind;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Shown (copyable) for errors so support can trace the request. */
  correlationId?: string;
  compact?: boolean;
}) {
  const Icon = STATE_ICON[kind];
  return (
    <div
      role={kind === 'error' || kind === 'offline' ? 'alert' : 'status'}
      className={cx(
        'flex flex-col items-center text-center',
        compact ? 'gap-2 py-6' : 'gap-3 py-12',
      )}
    >
      <span
        className={cx(
          'grid place-items-center rounded-full bg-surface-2 text-fg-muted',
          compact ? 'size-11' : 'size-14',
          kind === 'error' && 'text-negative',
        )}
      >
        <Icon aria-hidden className={compact ? 'size-5' : 'size-6'} />
      </span>
      <p className="m-0 text-[15px] font-semibold text-fg">{title}</p>
      {description && (
        <p className="m-0 max-w-sm text-[13px] leading-7 text-fg-muted">{description}</p>
      )}
      {correlationId && (
        <p className="m-0 text-[11px] text-fg-subtle">
          کد پیگیری: <bdi className="hx-ltr font-mono">{correlationId}</bdi>
        </p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/* ───────── Layout ───────── */

export function AppContainer({
  children,
  className,
  size = 'app',
}: {
  children: ReactNode;
  className?: string;
  size?: 'app' | 'content' | 'reading';
}) {
  const max = { app: 'max-w-[1440px]', content: 'max-w-[1200px]', reading: 'max-w-[720px]' }[size];
  return (
    <div className={cx('mx-auto w-full px-4 md:px-6 xl:px-8', max, className)}>{children}</div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 pb-6">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-medium text-accent-text">{eyebrow}</div>}
        <h1 className="m-0 text-2xl font-bold leading-tight text-fg lg:text-[28px]">{title}</h1>
        {description && (
          <p className="m-0 mt-2 max-w-2xl text-sm leading-7 text-fg-muted">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

export function SectionHeader({
  title,
  action,
  id,
}: {
  title: string;
  action?: ReactNode;
  id?: string;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 id={id} className="m-0 text-[15px] font-semibold text-fg-muted">
        {title}
      </h2>
      {action}
    </div>
  );
}

/* ───────── Data rows ───────── */

export function DataList({ children, label }: { children: ReactNode; label: string }) {
  return (
    <ul aria-label={label} className="m-0 list-none divide-y divide-line p-0">
      {children}
    </ul>
  );
}

export function DataRow({
  leading,
  title,
  subtitle,
  trailing,
  trailingSub,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  trailingSub?: ReactNode;
}) {
  return (
    <li className="flex min-h-16 items-center gap-3 py-3">
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-fg">{title}</div>
        {subtitle && <div className="mt-0.5 truncate text-xs text-fg-muted">{subtitle}</div>}
      </div>
      {(trailing || trailingSub) && (
        <div className="shrink-0 text-end">
          {trailing && <div className="text-sm font-semibold text-fg">{trailing}</div>}
          {trailingSub && <div className="mt-0.5 text-xs text-fg-muted">{trailingSub}</div>}
        </div>
      )}
    </li>
  );
}

/** Technical identifier / number, isolated LTR (TXID, address, IBAN, IDs). */
export function Ltr({
  children,
  mono = true,
  className,
}: {
  children: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return <bdi className={cx('hx-ltr', mono && 'font-mono', className)}>{children}</bdi>;
}
